
import React, { useState, useEffect } from 'react';
import { UserRole } from '../types';
import Logo from './Logo';

interface LoginProps {
  onLogin: (email: string, pass: string, role: UserRole) => void;
  onBack: () => void;
  error?: string | null;
}

const Login: React.FC<LoginProps> = ({ onLogin, onBack, error: externalError }) => {
  const [role, setRole] = useState<UserRole>(UserRole.OWNER);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (externalError) {
      setLocalError(externalError);
      setIsLoading(false); // Para o carregamento em caso de erro externo
      triggerShake();
    }
  }, [externalError]);

  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    
    if (!email || !password) {
      setLocalError('Preencha seu e-mail e sua senha secreta.');
      triggerShake();
      return;
    }

    setIsLoading(true);
    onLogin(email, password, role);
  };

  const handleInputChange = (setter: (val: string) => void, val: string) => {
    setLocalError(null);
    setter(val);
  };

  return (
    <div className="min-h-screen bg-brand-gradient flex flex-col items-center justify-center p-6 font-sans overflow-hidden">
      <div className={`w-full max-w-md bg-white rounded-[3.5rem] shadow-2xl overflow-hidden transition-all duration-300 ${isShaking ? 'animate-shake' : 'animate-in zoom-in duration-500'}`}>
        <div className="p-8 sm:p-12">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-8">
              <Logo className="h-16" />
            </div>
            <h2 className="font-title font-black text-brand-navy uppercase italic text-3xl tracking-tighter italic leading-none">Acesso Restrito</h2>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mt-3">Área do Empreendedor e Gestão</p>
          </div>

          {/* Toggle de Perfil */}
          <div className="flex bg-inputGray p-1.5 rounded-2xl mb-8">
            <button 
              disabled={isLoading}
              onClick={() => { setRole(UserRole.OWNER); setLocalError(null); }}
              className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${role === UserRole.OWNER ? 'bg-white text-brand-navy shadow-sm' : 'text-gray-400 opacity-50'}`}
            >
              Sou Lojista
            </button>
            <button 
              disabled={isLoading}
              onClick={() => { setRole(UserRole.ADMIN); setLocalError(null); }}
              className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${role === UserRole.ADMIN ? 'bg-brand-navy text-white shadow-sm' : 'text-gray-400 opacity-50'}`}
            >
              Administração
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest ml-4 mb-1">E-mail de Acesso</label>
              <input 
                disabled={isLoading}
                type="email" 
                placeholder="exemplo@email.com" 
                className={`w-full p-5 rounded-2xl bg-inputGray border-2 transition-all font-bold focus:ring-2 focus:ring-brand-orange outline-none ${localError ? 'border-red-100 text-red-900' : 'border-transparent text-brand-navy'}`}
                value={email} 
                onChange={e => handleInputChange(setEmail, e.target.value)} 
              />
            </div>
            <div>
              <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest ml-4 mb-1">Senha Secreta</label>
              <input 
                disabled={isLoading}
                type="password" 
                placeholder="••••••••" 
                className={`w-full p-5 rounded-2xl bg-inputGray border-2 transition-all font-bold focus:ring-2 focus:ring-brand-orange outline-none ${localError ? 'border-red-100 text-red-900' : 'border-transparent text-brand-navy'}`}
                value={password} 
                onChange={e => handleInputChange(setPassword, e.target.value)} 
              />
            </div>

            {localError && (
              <div className="bg-red-50 text-red-600 text-[10px] font-black uppercase p-4 rounded-xl text-center border border-red-100 animate-in slide-in-from-top-2 flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                {localError}
              </div>
            )}

            <button 
              type="submit" 
              disabled={isLoading}
              className={`w-full flex items-center justify-center gap-3 bg-brand-orange text-white font-black py-6 rounded-2xl hover:bg-brand-blue hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-widest text-xs shadow-xl shadow-brand-orange/20 ${isLoading ? 'opacity-80 cursor-not-allowed' : ''}`}
            >
              {isLoading && (
                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isLoading ? 'Verificando...' : 'Entrar no Painel'}
            </button>
          </form>

          <div className="mt-10 text-center">
            <button 
              disabled={isLoading}
              onClick={onBack}
              className="text-gray-300 hover:text-brand-navy text-[10px] font-black uppercase tracking-[0.2em] transition-all disabled:opacity-30"
            >
              ← Voltar ao Marketplace
            </button>
          </div>
        </div>
      </div>
      
      <p className="mt-8 text-white/40 text-[9px] font-black uppercase tracking-[0.3em]">
        Ambiente Seguro • Compre em Guarulhos 2024
      </p>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          75% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Login;
